import { action, computed, makeObservable, observable } from "mobx";
import { isNil } from "lodash";
import { MrbBaseListViewStore } from "mrb/core";
import { MrbTableViewStore } from "mrb/components";
import { MrbSelectStore } from "mrb/components/select";
import { MrbModalParams } from "mrb/common/models";
import { mrbDefaultFormats } from "mrb/common/formatting";
import { applicationDefaults, applicationErrorCodes } from "common/constants";
import { localizationService } from "common/localization";

const t = (key, params) => localizationService.t(`ADMINISTRATION.APP_RELEASE.BETA_CHANNEL.${key}`, params);

class BetaChannelViewStore extends MrbBaseListViewStore {
    @observable isSubmitting = false;

    @computed get isInitialized() {
        return this.gridStore.dataInitialized;
    }

    constructor(rootStore, { routeStore }) {
        super(rootStore, {
            queryConfig: {
                embed: "user",
                orderBy: "dateCreated",
                orderDirection: "desc",
                pageSize: 10,
                disableUpdateQueryParams: true,
            },
        });
        makeObservable(this);
        this.routeStore = routeStore;

        this.addUserModal = new MrbModalParams();
        this.userSelectStore = new MrbSelectStore({
            placeholder: t("MODAL.PLACEHOLDER"),
            isSearchable: true,
            isClearable: true,
            virtualized: false,
            initFetch: false,
            actions: {
                find: (searchQuery, page, rpp) =>
                    this.routeStore.findUsers({
                        searchQuery,
                        page,
                        rpp,
                        embed: "membership",
                        sort: "firstName|asc,lastName|asc",
                    }),
                mapItem: (user) => {
                    const membership = user.membership || {};
                    const firstName = user.firstName || membership.firstName;
                    const lastName = user.lastName || membership.lastName;
                    const email = user.email || membership.email;
                    return {
                        value: user.id,
                        label: `${firstName || ""} ${lastName || ""} - ${email}`.trim(),
                        isDisabled: false,
                        item: user,
                    };
                },
            },
        });

        this.setGridStore(
            new MrbTableViewStore(this.rootStore, this.queryUtility, {
                mapper: (item) => ({
                    ...item,
                    userName: this.getUserName(item.user),
                    userEmail: item.user ? item.user.email : null,
                }),
                actions: {
                    remove: {
                        onClick: (item) => this.confirmRemove(item),
                        label: "ADMINISTRATION.APP_RELEASE.BETA_CHANNEL.ACTIONS.REMOVE",
                    },
                },
                columns: [
                    { key: "userName", title: "ADMINISTRATION.APP_RELEASE.BETA_CHANNEL.COLUMNS.USER" },
                    { key: "userEmail", title: "ADMINISTRATION.APP_RELEASE.BETA_CHANNEL.COLUMNS.EMAIL" },
                    {
                        key: "dateCreated",
                        title: "ADMINISTRATION.APP_RELEASE.BETA_CHANNEL.COLUMNS.DATE_ADDED",
                        format: {
                            type: mrbDefaultFormats.date,
                            format: applicationDefaults.formats.dateTime,
                        },
                    },
                ],
            })
        );
    }

    find(params) {
        return this.routeStore.findBetaChannelUsers(params);
    }

    onFetchError() {
        this.rootStore.notificationStore.error(t("LOAD_ERROR"));
    }

    getUserName(user) {
        if (!user) {
            return null;
        }
        const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
        return fullName || user.displayName || user.userName;
    }

    confirmRemove = (item) => {
        const params = { user: item.userName || item.userEmail || item.userId };

        this.rootStore.confirmDialogStore.showConfirm({
            message: t("CONFIRM.REMOVE.MESSAGE", params),
            description: t("CONFIRM.REMOVE.DESCRIPTION", params),
            yesLabel: t("CONFIRM.REMOVE.YES"),
            noLabel: localizationService.t("ADMINISTRATION.APP_RELEASE.CONFIRM.NO"),
            onConfirm: async () => {
                try {
                    await this.routeStore.removeBetaChannelUser(item.id);
                    this.rootStore.notificationStore.success(t("REMOVE.SUCCESS", params));
                    this.queryUtility.fetch();
                } catch (err) {
                    this.rootStore.notificationStore.error(t("REMOVE.ERROR", params), err);
                }
            },
        });
    };

    @action.bound
    openAddUserModal() {
        this.userSelectStore.clear();
        this.addUserModal.open();
    }

    @action.bound
    setIsSubmitting(value) {
        this.isSubmitting = value;
    }

    @action.bound
    async addUser() {
        const userId = this.userSelectStore.value;
        if (isNil(userId) || this.isSubmitting) {
            return;
        }

        this.setIsSubmitting(true);
        try {
            await this.routeStore.addBetaChannelUser(userId);
            this.rootStore.notificationStore.success(t("ADD.SUCCESS"));
            this.addUserModal.close();
            this.userSelectStore.clear();
            this.queryUtility.fetch();
        } catch (err) {
            const isDuplicate =
                err && err.data && err.data.errorCode === applicationErrorCodes.betaChannel.userAlreadyInChannel;
            this.rootStore.notificationStore.error(t(isDuplicate ? "ADD.ALREADY_IN_CHANNEL" : "ADD.ERROR"), err);
        } finally {
            this.setIsSubmitting(false);
        }
    }
}

export default BetaChannelViewStore;
